import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Sparkles, Clock, Shield, Star, CheckCircle, ArrowRight, Home, 
  Calendar, Award, Users, Building2, MapPin, Phone, Leaf, Heart, Check
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { GoogleReviewsGrid, ReviewsCounter, GoogleRatingBadge, useGoogleReviews } from '../components/GoogleReviews';

// Helper hook to get region-aware links
const useRegionLinks = () => {
  const location = useLocation();
  const getBasePath = () => {
    if (location.pathname.startsWith('/ie')) return '/ie';
    if (location.pathname.startsWith('/pt')) return '/pt';
    return '';
  };
  const basePath = getBasePath();
  const region = location.pathname.startsWith('/ie') ? 'ie' : 'pt';
  const getLink = (path) => path === '/' ? (basePath || '/') : `${basePath}${path}`;
  return { getLink, basePath, region };
};

// Before/After Slider Component for Homepage
const BeforeAfterSliderHome = ({ beforeImage, afterImage, language }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const mouseMoveHandler = (e) => handleMove(e.clientX);
    const touchMoveHandler = (e) => handleMove(e.touches[0].clientX);
    
    if (isDragging) {
      window.addEventListener('mousemove', mouseMoveHandler);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', touchMoveHandler);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', touchMoveHandler);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-64 md:h-72 overflow-hidden rounded-xl cursor-ew-resize select-none shadow-lg"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* After Image (Background) */}
      <div className="absolute inset-0">
        <img src={afterImage} alt="After" className="w-full h-full object-cover" draggable="false" />
        <div className="absolute top-3 right-3 bg-[#1a5c3a] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {language === 'pt' ? 'DEPOIS' : 'AFTER'}
        </div>
      </div>

      {/* Before Image (Clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
        <img src={beforeImage} alt="Before" className="w-full h-full object-cover" draggable="false" />
        <div className="absolute top-3 left-3 bg-gray-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {language === 'pt' ? 'ANTES' : 'BEFORE'}
        </div>
      </div>

      {/* Slider Handle */}
      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-lg" style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-[#1a5c3a]">
          <div className="flex items-center gap-1">
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-r-[6px] border-r-[#1a5c3a] border-b-[5px] border-b-transparent" />
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[6px] border-l-[#1a5c3a] border-b-[5px] border-b-transparent" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Featured Before/After gallery items for homepage
const featuredGalleryItems = [
  {
    id: 4,
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/yo9ueg7x_deep1.webp',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/lrf0bfba_deep2.webp',
    titleEn: 'Behind the Stove Clean',
    titlePt: 'Limpeza Atrás do Fogão'
  },
  {
    id: 5,
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/sn5413vv_deep3.webp',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/iyepln2z_deep4.webp',
    titleEn: 'Oven Deep Clean',
    titlePt: 'Limpeza Profunda do Forno'
  },
  {
    id: 2,
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/l4ulkipd_deep5.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/na3t2sq0_deep6.jpeg',
    titleEn: 'Kitchen Counter Clean',
    titlePt: 'Limpeza de Bancada de Cozinha'
  },
  {
    id: 6,
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/qr18r4aq_deep7.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/hyriwets_deep8.jpeg',
    titleEn: 'Sofa Deep Clean',
    titlePt: 'Limpeza Profunda do Sofá'
  }
];

const HomePageIreland = () => {
  const { language } = useLanguage();
  const { getLink } = useRegionLinks();

  const getText = (en, pt) => language === 'pt' ? pt : en;
  const reviewsData = useGoogleReviews();

  // Service cards with images
  const services = [
    {
      icon: <Home size={24} />,
      title: getText('House Cleaning', 'Limpeza Doméstica'),
      description: getText(
        'Regular cleaning to keep your home fresh, clean and comfortable.',
        'Limpeza regular para manter a sua casa fresca, limpa e confortável.'
      ),
      image: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=400&h=300&fit=crop',
      link: '/gallery?category=house'
    },
    {
      icon: <Sparkles size={24} />,
      title: getText('Deep Cleaning', 'Limpeza Profunda'),
      description: getText(
        'A thorough clean that removes dirt and grime from every corner.',
        'Uma limpeza completa que remove sujidade de todos os cantos.'
      ),
      image: 'https://images.unsplash.com/photo-1631048501786-4e97f20eac71?w=400&h=300&fit=crop',
      link: '/gallery?category=deep'
    },
    {
      icon: <Calendar size={24} />,
      title: getText('End of Tenancy Cleaning', 'Limpeza Fim de Arrendamento'),
      description: getText(
        'Perfect for tenants and landlords. We help you get your deposit back.',
        'Perfeito para inquilinos e senhorios. Ajudamos a recuperar o depósito.'
      ),
      image: 'https://images.unsplash.com/photo-1502005097973-6a7082348e28?w=400&h=300&fit=crop',
      link: '/gallery?category=tenancy'
    },
    {
      icon: <Building2 size={24} />,
      title: getText('Commercial Cleaning', 'Limpeza Comercial'),
      description: getText(
        'Professional cleaning solutions for offices, shops and businesses.',
        'Soluções profissionais de limpeza para escritórios, lojas e empresas.'
      ),
      image: 'https://images.unsplash.com/photo-1631193816258-28b44b21e78b?w=400&h=300&fit=crop',
      link: '/gallery?category=commercial'
    },
    {
      icon: <MapPin size={24} />,
      title: getText('Airbnb Cleaning', 'Limpeza Airbnb'),
      description: getText(
        'Fast, reliable and efficient cleaning for short-term rental properties.',
        'Limpeza rápida, fiável e eficiente para propriedades de aluguer de curta duração.'
      ),
      image: 'https://images.unsplash.com/photo-1724582586529-62622e50c0b3?w=400&h=300&fit=crop',
      link: '/gallery?category=airbnb'
    }
  ];

  // Trust features for the bar below hero
  const trustFeatures = [
    {
      icon: <Shield size={20} />,
      title: getText('Public Liability Insurance', 'Seguro de Responsabilidade'),
    },
    {
      icon: <Leaf size={20} />,
      title: getText('Eco-Friendly Cleaning Products', 'Produtos Ecológicos'),
    },
    {
      icon: <Heart size={20} />,
      title: getText('Satisfaction Guaranteed', 'Satisfação Garantida'),
    },
    {
      icon: <Calendar size={20} />,
      title: getText('Flexible Scheduling', 'Horários Flexíveis'),
    }
  ];

  // Testimonials
  const testimonials = [
    {
      text: getText(
        'Excellent service! My house has never been so clean. Very professional team.',
        'Excelente serviço! A minha casa nunca esteve tão limpa. Equipa muito profissional.'
      ),
      author: 'Sarah, Dublin'
    },
    {
      text: getText(
        'Booked on the same day and they did an amazing deep clean.',
        'Reservei no mesmo dia e fizeram uma limpeza profunda incrível.'
      ),
      author: 'James, Dublin'
    },
    {
      text: getText(
        'Reliable, friendly and great value for money. Highly recommended!',
        'Fiáveis, simpáticos e excelente qualidade-preço. Altamente recomendado!'
      ),
      author: 'Emma, Dublin'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      
      {/* Hero Section */}
      <section className="relative min-h-screen lg:min-h-[90vh] flex flex-col lg:flex-row lg:items-center overflow-hidden">
        {/* Background - Desktop Split layout */}
        <div className="hidden lg:block absolute inset-0">
          {/* Full green base */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a5c3a] via-[#1a5c3a] to-[#15472d]" />
          
          {/* Right side - image with extended gradient overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-[55%]">
            <img 
              src="https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/fa2voecp_Criar_imagem_de_uma_mulher_com_o_logo_e_o_uniforme_delpmaspu.png"
              alt="Sancur Professional Cleaner"
              className="w-full h-full object-cover object-center"
            />
            {/* Extended gradient to blend seamlessly */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a5c3a] via-[#1a5c3a]/80 via-30% to-transparent" />
          </div>
        </div>

        {/* Mobile Background - Image with gradient overlay for immediate visibility */}
        <div className="lg:hidden absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/fa2voecp_Criar_imagem_de_uma_mulher_com_o_logo_e_o_uniforme_delpmaspu.png"
            alt="Sancur Professional Cleaner"
            className="w-full h-full object-cover object-[70%_30%]"
          />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a5c3a]/85 via-[#1a5c3a]/60 to-[#1a5c3a]/40" />
        </div>

        {/* Content - positioned left */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-xl lg:max-w-lg">
            {/* Rating Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6 border border-white/20">
              <Star size={16} fill="currentColor" className="text-[#d4a84b]" />
              <span className="font-semibold">5.0/5</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" className="text-[#d4a84b]" />
                ))}
              </div>
              <span className="text-white/80 text-sm ml-1 hidden sm:inline">
                {getText('Rated by customers in Dublin', 'Avaliado por clientes em Dublin')}
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              {getText('Professional', 'Serviços')}
              <br />
              {getText('Cleaning Services', 'Profissionais de')}
              <br />
              <span className="text-[#d4a84b]">
                {getText('in Dublin', 'Limpeza em Dublin')}
              </span>
            </h1>

            {/* Description */}
            <p className="text-lg text-white/80 mb-6">
              {getText(
                'Reliable, high-quality cleaning for homes and businesses. Same-day bookings available with no hidden fees.',
                'Limpeza fiável e de alta qualidade para casas e empresas. Reservas no mesmo dia disponíveis sem taxas ocultas.'
              )}
            </p>

            {/* Price Highlight */}
            <div className="mb-8">
              <span className="text-xl text-white/90">
                {getText('Cleaning from', 'Limpeza desde')}
              </span>
              <span className="text-4xl font-bold text-[#d4a84b] ml-2">€23</span>
              <span className="text-xl text-white/80">/hour</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to={getLink('/quote')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4a84b] text-white font-semibold rounded-lg hover:bg-[#c49a40] transition-all shadow-lg hover:shadow-xl"
                data-testid="hero-cta-quote"
              >
                <Sparkles size={20} />
                {getText('Get a Free Quote', 'Pedir Orçamento Grátis')}
                <ArrowRight size={18} />
              </Link>
              <a
                href="https://wa.me/353899502499"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a5c3a] font-semibold rounded-lg hover:bg-gray-100 transition-all"
                data-testid="hero-cta-whatsapp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 lg:gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <Shield size={20} className="text-[#d4a84b]" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{getText('Fully Insured', 'Totalmente Segurado')}</p>
                  <p className="text-xs text-white/60">{getText('for your peace of mind', 'para sua tranquilidade')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <Users size={20} className="text-[#d4a84b]" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{getText('Verified & Trained', 'Verificados e Treinados')}</p>
                  <p className="text-xs text-white/60">{getText('Professional Cleaners', 'Limpadores Profissionais')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <Clock size={20} className="text-[#d4a84b]" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{getText('Same-Day', 'No Mesmo Dia')}</p>
                  <p className="text-xs text-white/60">{getText('Bookings Available', 'Reservas Disponíveis')}</p>
                </div>
              </div>
            </div>

            {/* Reviews Counter */}
            <ReviewsCounter language={language} data={reviewsData} />
          </div>
        </div>

      </section>

      {/* Trust Bar */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
            {trustFeatures.map((feature, index) => (
              <div key={index} className="flex items-center justify-center gap-3 py-6 px-4">
                <div className="text-[#1a5c3a]">
                  {feature.icon}
                </div>
                <span className="text-sm font-medium text-gray-700">{feature.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50" id="before-after">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#d4a84b]/20 text-[#d4a84b] rounded-full text-sm font-bold mb-4">
              {getText('REAL RESULTS', 'RESULTADOS REAIS')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {getText('Before & After Our Services', 'Antes e Depois dos Nossos Serviços')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {getText(
                'See the transformation! Drag the slider to compare the results of our professional cleaning.',
                'Veja a transformação! Arraste o slider para comparar os resultados da nossa limpeza profissional.'
              )}
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {featuredGalleryItems.map((item) => (
              <div key={item.id} className="group">
                <BeforeAfterSliderHome
                  beforeImage={item.before}
                  afterImage={item.after}
                  language={language}
                />
                <p className="text-center mt-3 text-sm font-medium text-gray-700">
                  {language === 'pt' ? item.titlePt : item.titleEn}
                </p>
              </div>
            ))}
          </div>

          {/* CTA to Gallery */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              {getText(
                'Want to see more transformations? Visit our complete gallery!',
                'Quer ver mais transformações? Visite a nossa galeria completa!'
              )}
            </p>
            <Link
              to={getLink('/galeria')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a5c3a] text-white font-semibold rounded-full hover:bg-[#143d28] transition-colors shadow-lg hover:shadow-xl"
            >
              {getText('View Full Gallery', 'Ver Galeria Completa')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white" id="services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#1a5c3a]/10 text-[#1a5c3a] rounded-full text-sm font-medium mb-4">
              {getText('OUR SERVICES', 'NOSSOS SERVIÇOS')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {getText('Cleaning Solutions for Every Need', 'Soluções de Limpeza para Cada Necessidade')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {getText(
                'Tailored cleaning services to keep your home or business spotless.',
                'Serviços de limpeza personalizados para manter a sua casa ou empresa impecável.'
              )}
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={getLink(service.link)}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                {/* Image with icon overlay */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-3 left-3 w-12 h-12 bg-[#1a5c3a] rounded-xl flex items-center justify-center text-white shadow-lg">
                    {service.icon}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <h3 className="font-heading font-bold text-gray-900 mb-2 group-hover:text-[#1a5c3a] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-[#1a5c3a] font-medium text-sm group-hover:gap-2 transition-all">
                    {getText('Learn more', 'Saber mais')}
                    <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-6 bg-[#1a5c3a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Sparkles size={28} className="text-[#d4a84b]" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-white">
                  {getText('Ready for a Cleaner Space?', 'Pronto para um Espaço Mais Limpo?')}
                </h3>
                <p className="text-white/80">
                  {getText('Get your free quote today and experience the Sancur difference.', 'Peça o seu orçamento grátis hoje e experimente a diferença Sancur.')}
                </p>
              </div>
            </div>
            <Link
              to={getLink('/quote')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a5c3a] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              {getText('Get a Free Quote', 'Pedir Orçamento Grátis')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8 items-start">
            {/* Google Rating */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Google Rating</p>
                  <GoogleRatingBadge language={language} data={reviewsData} />
                </div>
              </div>
              <a
                href={reviewsData.maps_url ? reviewsData.maps_url : "https://g.page/r/CZuJ-aERsocTEAE/review"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a5c3a] text-white font-medium rounded-lg hover:bg-[#15472d] transition-colors text-sm"
              >
                <Star size={16} />
                {getText('Leave us a Review', 'Deixe-nos uma Avaliação')}
              </a>
            </div>

            {/* Testimonials: real Google reviews + manual; static fallback */}
            <GoogleReviewsGrid language={language} data={reviewsData} fallback={testimonials} />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#1a5c3a]/10 text-[#1a5c3a] rounded-full text-sm font-medium mb-4">
              {getText('WHY CHOOSE US', 'PORQUÊ ESCOLHER-NOS')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {getText('The Sancur Difference', 'A Diferença Sancur')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a5c3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-[#1a5c3a]" />
              </div>
              <h3 className="font-heading font-bold text-gray-900 mb-2">{getText('Fully Insured', 'Totalmente Segurado')}</h3>
              <p className="text-sm text-gray-600">{getText('Complete peace of mind with our comprehensive insurance coverage.', 'Total tranquilidade com a nossa cobertura de seguro abrangente.')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a5c3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award size={32} className="text-[#1a5c3a]" />
              </div>
              <h3 className="font-heading font-bold text-gray-900 mb-2">{getText('Trained Professionals', 'Profissionais Treinados')}</h3>
              <p className="text-sm text-gray-600">{getText('Our team undergoes rigorous training and background checks.', 'A nossa equipa passa por formação rigorosa e verificação de antecedentes.')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a5c3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Leaf size={32} className="text-[#1a5c3a]" />
              </div>
              <h3 className="font-heading font-bold text-gray-900 mb-2">{getText('Eco-Friendly', 'Ecológico')}</h3>
              <p className="text-sm text-gray-600">{getText('We use environmentally safe cleaning products.', 'Usamos produtos de limpeza seguros para o ambiente.')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a5c3a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart size={32} className="text-[#1a5c3a]" />
              </div>
              <h3 className="font-heading font-bold text-gray-900 mb-2">{getText('Satisfaction Guaranteed', 'Satisfação Garantida')}</h3>
              <p className="text-sm text-gray-600">{getText("Not happy? We'll re-clean for free.", 'Não está satisfeito? Limpamos novamente grátis.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1a5c3a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            {getText('Ready to Experience a Cleaner Home?', 'Pronto para Experienciar uma Casa Mais Limpa?')}
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            {getText(
              'Book your professional cleaning service today. No hidden fees, flexible scheduling, and 100% satisfaction guaranteed.',
              'Reserve o seu serviço de limpeza profissional hoje. Sem taxas ocultas, horários flexíveis e satisfação 100% garantida.'
            )}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={getLink('/quote')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4a84b] text-white font-semibold rounded-lg hover:bg-[#c49a40] transition-all shadow-lg"
            >
              <Sparkles size={20} />
              {getText('Get Your Free Quote', 'Peça o Seu Orçamento Grátis')}
              <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+353899502499"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30"
            >
              <Phone size={20} />
              +353 89 950 2499
            </a>
          </div>
        </div>
      </section>

      {/* Service Areas Map */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#1a5c3a]/10 px-4 py-2 rounded-full mb-4">
              <MapPin size={18} className="text-[#1a5c3a]" />
              <span className="text-sm font-medium text-[#1a5c3a]">
                {getText('Service Areas', 'Áreas de Atendimento')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {getText('We Cover All of Dublin', 'Cobrimos Todo Dublin')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {getText(
                'We provide our cleaning services across all Dublin areas accessible by public transport. If a bus can get there, so can we!',
                'Prestamos os nossos serviços de limpeza em todas as áreas de Dublin acessíveis por transporte público. Se o autocarro chega lá, nós também chegamos!'
              )}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Map */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d152515.98633442845!2d-6.2673028!3d53.3242381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sie!4v1703123456789!5m2!1sen!2sie"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dublin Service Area Map"
                className="w-full"
              />
              {/* Overlay badge */}
              <div className="absolute top-4 left-4 bg-[#1a5c3a] text-white px-4 py-2 rounded-lg shadow-lg">
                <div className="flex items-center gap-2">
                  <MapPin size={16} />
                  <span className="font-semibold text-sm">Dublin, Ireland</span>
                </div>
              </div>
            </div>

            {/* Areas List */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#1a5c3a] rounded-lg flex items-center justify-center">
                    <MapPin size={16} className="text-white" />
                  </div>
                  {getText('Areas We Serve', 'Áreas que Atendemos')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Dublin City Centre',
                    'Dublin 1 - 24',
                    'Dún Laoghaire',
                    'Blackrock',
                    'Sandyford',
                    'Tallaght',
                    'Blanchardstown',
                    'Swords',
                    'Howth',
                    'Malahide',
                    'Clondalkin',
                    'Lucan'
                  ].map((area, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700">
                      <Check size={16} className="text-[#1a5c3a] flex-shrink-0" />
                      <span className="text-sm">{area}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a5c3a]/5 border border-[#1a5c3a]/20 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#1a5c3a] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🚌</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {getText('Public Transport Accessible', 'Acessível por Transporte Público')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {getText(
                        'We serve all Dublin areas accessible by Dublin Bus, Luas, or DART. Not sure if we cover your area? Just ask!',
                        'Atendemos todas as áreas de Dublin acessíveis por Dublin Bus, Luas ou DART. Não tem a certeza se cobrimos a sua área? Pergunte-nos!'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <Link
                to={getLink('/quote')}
                className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 bg-[#1a5c3a] text-white font-semibold rounded-xl hover:bg-[#15472d] transition-all shadow-lg"
              >
                <Sparkles size={20} />
                {getText('Get a Quote for Your Area', 'Peça Orçamento para a Sua Área')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePageIreland;
